import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VEHICLE_FIELDS, deviceFieldsFor, gpsFlow, isMoto, slotsFor } from "@/lib/gps";
import { GpsExecuted, GpsInstallConfig, Head } from "@/components/gps/GpsInstallConfig";
import { UnitTargets } from "@/components/gps/UnitTargets";
import { InventoryDevicePick } from "@/components/gps/InventoryDevicePick";

const sel = "h-10 w-full rounded-md border border-border bg-transparent px-2 text-sm";
const PLAN_GROUPS = [["Planes GPS", "plan_gps"], ["Videovigilancia Móvil", "kit_vm"], ["Dashcam", "dashcam"]];

const Txt = ({ label, value, onChange, disabled, testid }) => (
  <div className="space-y-1">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    <Input className="h-10" value={value || ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </div>
);

const Device = ({ d, n, editable: canEdit, configEditable, onChange, multi, fixed, woId, technicianId }) => { const editable = canEdit && !fixed; return (
  <div className="space-y-2 rounded-lg border border-border/70 p-3" data-testid={`gps-unit-${n}-device-${d.component_key}-${d.index}`}>
    <div className="text-sm font-medium">{d.component_label}{multi ? ` ${d.index + 1}` : ""}</div>
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Modelo previsto</Label>
        <select className={sel} value={d.planned_model || ""} disabled={!configEditable} onChange={(e) => onChange({ ...d, planned_model: e.target.value })} data-testid={`gps-unit-${n}-device-${d.component_key}-${d.index}-planned-model`}>
          <option value="">{configEditable ? "Seleccionar" : "Sin definir"}</option>
          {(d.allowed_models || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div className="space-y-1">
        <Label className="text-[11px] text-muted-foreground">Modelo instalado</Label>
        <select className={sel} value={d.model} disabled={!editable} onChange={(e) => onChange({ ...d, model: e.target.value })} data-testid={`gps-unit-${n}-device-${d.component_key}-${d.index}-model`}>
          <option value="">Seleccionar</option>
          {(fixed && d.model && !(d.allowed_models || []).includes(d.model) ? [d.model, ...(d.allowed_models || [])] : d.allowed_models || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      {editable && <InventoryDevicePick woId={woId} technicianId={technicianId} d={d} onPick={onChange} testid={`gps-unit-${n}-device-${d.component_key}-${d.index}-inventory`} />}
      {deviceFieldsFor(d).map(([f, l]) => <Txt key={f} label={l} value={d[f]} disabled={!editable} onChange={(v) => onChange({ ...d, [f]: v })} testid={`gps-unit-${n}-device-${d.component_key}-${d.index}-${f}`} />)}
    </div>
    {fixed && <p className="text-xs text-muted-foreground">Equipo en resguardo definido por Administración.</p>}
  </div>
); };

const withSource = (unit, d, i) => (unit.install_source === "existente" && i === 0 && unit.source_device
  ? { ...d, model: unit.source_device.model, imei: unit.source_device.imei, sim_iccid: unit.source_device.sim_iccid } : d);

const Accessories = ({ rows, n, editable, onChange }) => (
  <div className="space-y-2">
    {rows.map((a, i) => (
      <div key={i} className="grid grid-cols-[1fr_80px_auto] gap-2 sm:grid-cols-[1fr_90px_1fr_auto]" data-testid={`gps-unit-${n}-accessory-${i}`}>
        <Input placeholder="Accesorio" value={a.name} disabled={!editable} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} data-testid={`gps-unit-${n}-accessory-${i}-name`} />
        <Input type="number" min="1" value={a.quantity} disabled={!editable} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} data-testid={`gps-unit-${n}-accessory-${i}-qty`} />
        <Input className="col-span-2 sm:col-span-1" placeholder="Notas" value={a.notes} disabled={!editable} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, notes: e.target.value } : x)))} data-testid={`gps-unit-${n}-accessory-${i}-notes`} />
        {editable && <Button size="icon" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`gps-unit-${n}-accessory-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>}
      </div>
    ))}
    {!rows.length && <p className="text-sm text-muted-foreground">Sin accesorios.</p>}
    {editable && <Button size="sm" variant="outline" onClick={() => onChange([...rows, { name: "", quantity: 1, notes: "" }])} data-testid={`gps-unit-${n}-add-accessory`}><Plus className="mr-1 h-4 w-4" />Accesorio</Button>}
  </div>
);

export const GpsUnitCard = ({ n, unit, plans, editable, configEditable, planLocked, onChange, actions, footer, flow = gpsFlow("instalacion"), woId, technicianId }) => {
  const options = unit.plan && !plans.some((p) => p.id === unit.plan.id) ? [unit.plan, ...plans] : plans;
  const choosePlan = (id) => { const plan = options.find((p) => p.id === id) || null; onChange({ ...unit, plan, devices: slotsFor(plan, unit.devices) }); };
  const multi = (key) => (unit.plan?.components || []).find((c) => c.key === key)?.quantity > 1;
  return (
    <div className="space-y-5 rounded-xl border border-border bg-card/40 p-4" data-testid={`gps-unit-${n}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div className="font-display text-lg">Unidad {n + 1}</div>
        {flow.install ? <div className="min-w-[200px] flex-1 space-y-1">
          <Head>Plan</Head>
          <select className={sel} value={unit.plan?.id || ""} disabled={!editable || planLocked} onChange={(e) => choosePlan(e.target.value)} data-testid={`gps-unit-${n}-plan`}>
            <option value="">Seleccionar plan</option>
            {PLAN_GROUPS.map(([label, cat]) => { const list = options.filter((p) => (p.category || "plan_gps") === cat); return list.length > 0 && (
              <optgroup key={label} label={label}>{list.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</optgroup>); })}
          </select>
        </div> : <div className="flex-1" />}
        {actions}
      </div>
      <UnitTargets unit={unit} n={n} flow={flow} plans={plans} planner={configEditable} editable={editable} onChange={onChange} />
      {flow.install && <>
        <GpsInstallConfig unit={unit} n={n} configEditable={configEditable} onChange={onChange} />
        <div className="space-y-2 border-t border-border pt-4">
          <Head>{{ kit_vm: "Equipo de Videovigilancia Móvil", dashcam: "Dashcam" }[unit.plan?.category] || "Dispositivo GPS"}</Head>
          {!unit.plan ? <p className="text-sm text-muted-foreground">Selecciona un plan para ver los dispositivos requeridos.</p>
            : <div className="grid gap-3 md:grid-cols-2">{unit.devices.map((d, i) => <Device key={`${d.component_key}-${d.index}`} d={withSource(unit, d, i)} n={n} editable={editable} configEditable={configEditable} multi={multi(d.component_key)} fixed={unit.install_source === "existente" && i === 0} woId={woId} technicianId={technicianId} onChange={(nd) => onChange({ ...unit, devices: unit.devices.map((x) => (x === d ? nd : x)) })} />)}</div>}
        </div>
        <GpsExecuted unit={unit} n={n} editable={editable} onChange={onChange} />
      </>}
      <div className="space-y-2 border-t border-border pt-4">
        <Head>{flow.combo ? "Vehículo de origen" : "Vehículo / unidad"}</Head>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {VEHICLE_FIELDS.map(([f, l]) => <Txt key={f} label={l} value={unit.vehicle?.[f]} disabled={!editable} onChange={(v) => onChange({ ...unit, vehicle: { ...unit.vehicle, [f]: v } })} testid={`gps-unit-${n}-vehicle-${f}`} />)}
        </div>
      </div>
      {flow.install && !(isMoto(unit.plan) && !unit.accessories?.length) && <div className="space-y-2 border-t border-border pt-4">
        <Head>Accesorios</Head>
        <Accessories rows={unit.accessories || []} n={n} editable={editable} onChange={(accessories) => onChange({ ...unit, accessories })} />
      </div>}
      {footer}
    </div>
  );
};
